import React from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import churchForms from '../../services/api/churchForms';
import ComunidadNetworkCard from 'src/containers/comunidad-network/catalog/ComunidadNetworkCard';
import { Input, Icon, Dropdown, Button } from 'semantic-ui-react';
import CatalogDetail from 'src/containers/comunidad-network/catalog/CatalogDetail';
import { shuffle, searchSelectProps } from '../../components/helpers';
import { NetworkCatalogPage } from 'src/assets/styles/components/network';
import {
  useRecaptchaToken,
  isCaptchaRequired,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';
import { NETWORK_CATEGORY_OPTIONS } from 'src/containers/comunidad-network/networkCategories';
import { jobPostingsFromRecord } from 'src/containers/comunidad-network/form/networkFormUtils';

const DISCLAIMER_HIDE_KEY = 'catalogo-red-empresas-hide-disclaimer';
const CATALOG_PAGE_SIZE = 50;

const companyHasJobs = (network) =>
  jobPostingsFromRecord(network?.jobPostings).some(
    (posting) => posting.title.trim() || posting.description.trim()
  );

const JobsLegend = ({ active, onClick }) => (
  <div className={`catalog-jobs-legend${active ? ' is-active' : ''}`}>
    {active ? (
      <>
        <span className="catalog-jobs-legend__label">
          <span className="catalog-jobs-legend__dot" aria-hidden="true" />
          Viendo solo Empresas con vacantes.
        </span>
        <button type="button" className="catalog-jobs-legend__clear" onClick={onClick}>
          Ver todas
        </button>
      </>
    ) : (
      <button
        type="button"
        className="catalog-jobs-legend__toggle"
        onClick={onClick}
        aria-pressed={false}
      >
        <span className="catalog-jobs-legend__dot" aria-hidden="true" />
        Ver solo empresas con vacantes.
      </button>
    )}
  </div>
);

const CatalogPager = ({ page, pageCount, onPage }) => {
  if (pageCount <= 1) return null;

  const pages = Array.from({ length: pageCount }, (_, index) => index + 1);

  return (
    <nav className="catalog-pager" aria-label="Páginas del directorio">
      <button type="button" onClick={() => onPage(page - 1)} disabled={page <= 1}>
        Anterior
      </button>
      {pages.map((pageNumber) => (
        <button
          key={pageNumber}
          type="button"
          className={pageNumber === page ? 'is-current' : undefined}
          aria-current={pageNumber === page ? 'page' : undefined}
          onClick={() => onPage(pageNumber)}
        >
          {pageNumber}
        </button>
      ))}
      <button type="button" onClick={() => onPage(page + 1)} disabled={page >= pageCount}>
        Siguiente
      </button>
    </nav>
  );
};

const ComunidadNetworkCatalog = () => {
  const [comunidadNetworks, set_ComunidadNetworks] = React.useState([]);
  const [loading, set_loading] = React.useState(true);
  const [selectedId, setSelectedId] = React.useState(null);
  const [keyword, setKeyword] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [filters, setFilters] = React.useState({ keyword: '', category: '' });
  const [showDisclaimer, setShowDisclaimer] = React.useState(
    () => localStorage.getItem(DISCLAIMER_HIDE_KEY) !== '1'
  );
  const [jobsOnly, setJobsOnly] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const shellRef = React.useRef(null);
  const getCaptchaToken = useRecaptchaToken('all_comunidad_network');

  const applySearch = React.useCallback(
    (next = {}) => {
      setFilters({
        keyword: (next.keyword ?? keyword).trim(),
        category: next.category ?? category,
      });
    },
    [keyword, category]
  );

  React.useEffect(() => {
    let cancelled = false;

    async function getInfo() {
      set_loading(true);

      const captchaRequired = isCaptchaRequired();
      const captcha = await getCaptchaToken({ skip: !captchaRequired });
      if (captchaRequired && !captcha) {
        if (!cancelled) set_loading(false);
        Swal.fire(RECAPTCHA_SECURITY_ERROR);
        return;
      }

      try {
        const response = await churchForms.getComunidadNetwork({
          published: 'Si',
          keyword: filters.keyword,
          category: filters.category || undefined,
          catalog: true,
          ...(captchaRequired ? { captcha } : {}),
        });

        if (cancelled) return;

        if (response.data.success) {
          const networks = shuffle(response.data.network || []);
          set_ComunidadNetworks(networks);
          setSelectedId((current) =>
            current && networks.some((network) => network.id === current) ? current : null
          );
        } else {
          set_ComunidadNetworks([]);
          setSelectedId(null);
        }
      } catch (error) {
        if (!cancelled && error.response) Swal.fire(error.response.data.message);
        if (!cancelled) {
          set_ComunidadNetworks([]);
          setSelectedId(null);
        }
      } finally {
        if (!cancelled) set_loading(false);
      }
    }

    getInfo();
    return () => {
      cancelled = true;
    };
  }, [filters, getCaptchaToken]);

  const onCategoryChange = (value) => {
    const nextCategory = value || '';
    setCategory(nextCategory);
    applySearch({ category: nextCategory });
  };

  React.useEffect(() => {
    setPage(1);
  }, [filters, jobsOnly]);

  const filteredNetworks = jobsOnly ? comunidadNetworks.filter(companyHasJobs) : comunidadNetworks;
  const pageCount = Math.max(1, Math.ceil(filteredNetworks.length / CATALOG_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * CATALOG_PAGE_SIZE;
  const pageNetworks = filteredNetworks.slice(pageStart, pageStart + CATALOG_PAGE_SIZE);
  const selectedNetwork = filteredNetworks.find((network) => network.id === selectedId) || null;
  const hasSelection = Boolean(selectedNetwork);
  const showCategoryOnCards = !filters.category;

  const goToPage = (nextPage) => {
    if (nextPage === currentPage || nextPage < 1 || nextPage > pageCount) return;
    setPage(nextPage);
    window.requestAnimationFrame(() => {
      shellRef.current?.scrollIntoView({ block: 'start' });
      document.querySelector('.catalog-list-panel')?.scrollTo({ top: 0 });
    });
  };

  return (
    <NetworkCatalogPage>
      <div className="catalog-shell" ref={shellRef}>
        <header className="catalog-header">
          <h1>Directorio de Empresas</h1>
          <p>Explora empresas, vendedores y servicios publicados por miembros de Comunidad.</p>
        </header>
        <div className="catalog-edit-link-row">
          <Link to="/inscripcion-red-empresas" className="catalog-edit-link">
            crear o modificar tu negocio
          </Link>
        </div>
        <div className="catalog-toolbar">
          <div className="catalog-search">
            <Input
              icon={<Icon name="search" />}
              iconPosition="left"
              loading={loading}
              name="keyword"
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applySearch();
              }}
              value={keyword}
              placeholder="Buscar por empresa, producto o servicio"
              aria-label="Buscar en el directorio"
            />
          </div>
          <div className="catalog-category">
            <Dropdown
              placeholder="Todas las categorías"
              selection
              clearable
              aria-label="Filtrar por categoría"
              {...searchSelectProps({
                value: category || null,
                handler: (e, { value }) => onCategoryChange(value),
                options: NETWORK_CATEGORY_OPTIONS,
              })}
            />
          </div>
          <Button
            type="button"
            primary
            className="catalog-search-button"
            onClick={() => applySearch()}
          >
            Buscar
          </Button>
          <p className="catalog-count">
            {loading
              ? 'Buscando...'
              : `${filteredNetworks.length} ${
                  filteredNetworks.length === 1 ? 'resultado' : 'resultados'
                }`}
          </p>
        </div>

        {showDisclaimer ? (
          <aside className="catalog-disclaimer" aria-label="Aviso del directorio">
            <button
              type="button"
              className="catalog-disclaimer__accept"
              onClick={() => setShowDisclaimer(false)}
            >
              Aceptar
            </button>
            <div className="catalog-disclaimer__body">
              <p>
                La Iglesia Comunidad Cristiana de Fe y Viento Fresco solo está refiriendo una base
                de datos de emprendedores, pero en ningún caso los está recomendando, ni se hace
                responsable de ninguna transacción comercial, laboral o civil entre los mismos.
              </p>
              <p>
                Oramos a Dios para que entre los emprendedores crezcan las buenas relaciones y
                obtengas bendiciones en todo, basados en Gálatas 6:10 “Así que, según tengamos
                oportunidad, hagamos bien a todos, y mayormente a los de la familia de la fe”.
              </p>
            </div>
            <button
              type="button"
              className="catalog-disclaimer__never"
              onClick={() => {
                localStorage.setItem(DISCLAIMER_HIDE_KEY, '1');
                setShowDisclaimer(false);
              }}
            >
              No volver a mostrar
            </button>
          </aside>
        ) : (
          <div className="catalog-disclaimer-show">
            <button
              type="button"
              className="catalog-disclaimer-show__link"
              onClick={() => {
                localStorage.removeItem(DISCLAIMER_HIDE_KEY);
                setShowDisclaimer(true);
              }}
            >
              Mostrar aviso de habeas data
            </button>
          </div>
        )}

        {loading && comunidadNetworks.length === 0 ? (
          <p className="catalog-loading catalog-loading--page">Cargando directorio...</p>
        ) : filteredNetworks.length === 0 ? (
          <>
            <JobsLegend active={jobsOnly} onClick={() => setJobsOnly((current) => !current)} />
            <p className="catalog-empty catalog-empty--page">
              {jobsOnly
                ? 'No se encontraron empresas con vacantes.'
                : 'No se encontraron empresas con esa búsqueda.'}
            </p>
          </>
        ) : hasSelection ? (
          <div className="catalog-workspace">
            <div className="catalog-list-panel is-hidden-mobile">
              <JobsLegend active={jobsOnly} onClick={() => setJobsOnly((current) => !current)} />
              <ul className="catalog-list">
                {pageNetworks.map((network) => (
                  <li key={network.id}>
                    <ComunidadNetworkCard
                      network={network}
                      selected={network.id === selectedId}
                      showCategory={showCategoryOnCards}
                      onSelect={setSelectedId}
                    />
                  </li>
                ))}
              </ul>
              <CatalogPager page={currentPage} pageCount={pageCount} onPage={goToPage} />
            </div>

            <aside className="catalog-detail-panel">
              <CatalogDetail
                inputs={selectedNetwork}
                openJobs={jobsOnly}
                onBack={() => setSelectedId(null)}
              />
            </aside>
          </div>
        ) : (
          <>
            <JobsLegend active={jobsOnly} onClick={() => setJobsOnly((current) => !current)} />
            <ul className="catalog-grid">
              {pageNetworks.map((network) => (
                <li key={network.id}>
                  <ComunidadNetworkCard
                    network={network}
                    showCategory={showCategoryOnCards}
                    onSelect={setSelectedId}
                  />
                </li>
              ))}
            </ul>
            <CatalogPager page={currentPage} pageCount={pageCount} onPage={goToPage} />
          </>
        )}
      </div>
    </NetworkCatalogPage>
  );
};

export default ComunidadNetworkCatalog;
